import { Slot } from "@radix-ui/react-slot";
import React from "react";

type ItemRecord = {
  id: string;
  disabled: boolean;
  onSelect?: () => void;
};

type ListNavigationContextValue = {
  activeId: string | null;
  listId: string;
  registerItem: (item: ItemRecord) => void;
  unregisterItem: (id: string) => void;
  updateItem: (item: ItemRecord) => void;
  setActiveId: (id: string | null) => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
};

const ListNavigationContext = React.createContext<ListNavigationContextValue | null>(null);

const useListNavigationContext = () => {
  const context = React.useContext(ListNavigationContext);
  if (!context) throw new Error("ListNavigation compound components must be used within ListNavigation");
  return context;
};

const ListNavigation = ({ children, value, defaultValue = null, onValueChange, onItemActivated, loop = true }: {
  children: React.ReactNode;
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (id: string | null) => void;
  onItemActivated?: (id: string | null) => void;
  loop?: boolean;
}) => {
  const listId = React.useId();
  const isControlled = value !== undefined;
  const [internalActiveId, setInternalActiveId] = React.useState<string | null>(defaultValue);
  const activeId = isControlled ? (value ?? null) : internalActiveId;
  const itemsRef = React.useRef<ItemRecord[]>([]);

  const setActiveId = React.useCallback((nextId: string | null) => {
    if (!isControlled) setInternalActiveId(nextId);
    onValueChange?.(nextId);
    onItemActivated?.(nextId);
  }, [isControlled, onItemActivated, onValueChange]);

  const registerItem = React.useCallback((item: ItemRecord) => {
    const index = itemsRef.current.findIndex((entry) => entry.id === item.id);
    if (index >= 0) itemsRef.current[index] = item;
    else itemsRef.current.push(item);
  }, []);

  const unregisterItem = React.useCallback((id: string) => {
    itemsRef.current = itemsRef.current.filter((item) => item.id !== id);
  }, []);

  const updateItem = React.useCallback((item: ItemRecord) => {
    const index = itemsRef.current.findIndex((entry) => entry.id === item.id);
    if (index >= 0) itemsRef.current[index] = item;
  }, []);

  const move = React.useCallback((direction: 1 | -1) => {
    const enabled = itemsRef.current.filter((item) => !item.disabled);
    if (enabled.length === 0) return;
    const currentIndex = enabled.findIndex((item) => item.id === activeId);
    if (currentIndex < 0) return setActiveId(enabled[0].id);
    const nextIndex = currentIndex + direction;
    if (loop) return setActiveId(enabled[(nextIndex + enabled.length) % enabled.length].id);
    if (nextIndex < 0 || nextIndex >= enabled.length) return;
    setActiveId(enabled[nextIndex].id);
  }, [activeId, loop, setActiveId]);

  const handleInputKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowDown") return event.preventDefault(), move(1);
    if (event.key === "ArrowUp") return event.preventDefault(), move(-1);
    if (event.key === "Home") {
      event.preventDefault();
      return setActiveId(itemsRef.current.find((item) => !item.disabled)?.id ?? null);
    }
    if (event.key === "End") {
      event.preventDefault();
      const reversed = [...itemsRef.current].reverse();
      return setActiveId(reversed.find((item) => !item.disabled)?.id ?? null);
    }
    if (event.key === "Enter" && activeId) {
      const item = itemsRef.current.find((entry) => entry.id === activeId);
      if (item && !item.disabled) {
        event.preventDefault();
        item.onSelect?.();
      }
    }
  }, [activeId, move, setActiveId]);

  const contextValue = React.useMemo(() => ({
    activeId,
    listId,
    registerItem,
    unregisterItem,
    updateItem,
    setActiveId,
    handleInputKeyDown,
  }), [activeId, listId, registerItem, unregisterItem, updateItem, setActiveId, handleInputKeyDown]);

  return (
    <ListNavigationContext.Provider value={contextValue}>
      <div data-list-navigation-root="" data-list-navigation-id={listId}>
        {children}
      </div>
    </ListNavigationContext.Provider>
  );
};

const Input = ({ asChild = false, onKeyDown, ...props }: React.ComponentPropsWithoutRef<"input"> & { asChild?: boolean }) => {
  const context = useListNavigationContext();
  const Comp = asChild ? Slot : "input";
  return (
    <Comp
      role="combobox"
      data-list-navigation-input=""
      data-list-navigation-id={context.listId}
      aria-controls={context.listId}
      aria-activedescendant={context.activeId ?? undefined}
      aria-autocomplete="list"
      onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
        context.handleInputKeyDown(event);
        onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
      }}
      {...props}
    />
  );
};

const List = ({ asChild = false, ...props }: React.ComponentPropsWithoutRef<"div"> & { asChild?: boolean }) => {
  const context = useListNavigationContext();
  const Comp = asChild ? Slot : "div";
  return <Comp id={context.listId} role="listbox" data-list-navigation-list="" data-list-navigation-id={context.listId} {...props} />;
};

const Item = ({ id, disabled = false, asChild = false, onSelect, onMouseEnter, onClick, ...props }: React.ComponentPropsWithoutRef<"div"> & {
  id: string;
  disabled?: boolean;
  asChild?: boolean;
  onSelect?: () => void;
}) => {
  const context = useListNavigationContext();
  const Comp = asChild ? Slot : "div";
  const isActive = context.activeId === id;

  React.useEffect(() => {
    context.registerItem({ id, disabled, onSelect });
    return () => context.unregisterItem(id);
  }, [context, id, disabled, onSelect]);

  React.useEffect(() => {
    context.updateItem({ id, disabled, onSelect });
  }, [context, id, disabled, onSelect]);

  return (
    <Comp
      id={id}
      role="option"
      data-list-navigation-item=""
      data-item-id={id}
      data-list-navigation-id={context.listId}
      aria-selected={isActive}
      aria-disabled={disabled || undefined}
      data-active={isActive ? "true" : "false"}
      data-disabled={disabled ? "true" : "false"}
      onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
        if (!disabled) context.setActiveId(id);
        onMouseEnter?.(event);
      }}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
          context.setActiveId(id);
          onSelect?.();
        }
        onClick?.(event);
      }}
      {...props}
    />
  );
};

export default Object.assign(ListNavigation, { Input, List, Item });
